import * as React from 'react';
import { useRef, useCallback } from 'react';
import './style.scss';
import { getViaAuth, responseValidator } from 'apis/api';
import { Col, Row, Spin, Switch } from 'antd';
import { Select } from 'components/select/select';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUserById, updateUser } from 'core/repositories/user';
import { API } from 'apis';
import { Input, inputType } from 'components/input/input';
import { Button, buttonType } from 'components/button/button';
import { DefaultOptionType } from 'antd/es/select';
import { Level, UserDetail } from 'core/domain';
import { Card } from 'components/card/card';
import Dragger from 'antd/es/upload/Dragger';
import { toast } from 'react-hot-toast';
import { EyeInvisibleOutlined, EyeOutlined, FileOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/es/upload';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';

import moment from 'jalali-moment';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';

const { useState, useEffect } = React;

export const EditUserForm: React.FC = () => {
    // States
    const [isCardLoading, setIsCardLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [userData, setUserData] = useState<UserDetail>();
    const [roles, setRoles] = useState<DefaultOptionType[]>([]);
    const [levels, setLevels] = useState<DefaultOptionType[]>([]);
    const [levelsData, setLevelsData] = useState<Level[]>();
    const [isPasswordHidden, setIsPasswordHidden] = useState<boolean>(true);
    // File upload props with useCallback to avoid unnecessary rerenders
    const fileUploadProps = useCallback(
        () => ({
            name: 'file-uploader',
            className: 'file-uploader',
            onRemove: (file: any) => {
                setUserData((prev) => ({
                    ...prev,
                    selfiePhoto: prev?.selfiePhoto?.filter((m: any) => m.type !== 'file'),
                }));
            },
            beforeUpload: (file: RcFile) => {
                const isCorrectFormat =
                    file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg';
                if (!isCorrectFormat) toast.error(`فرمت تصویر آپلود شده صحیح نمی باشد.`);
                else
                    setUserData((prev) => ({
                        ...prev,
                        selfiePhoto: [{ file: file, type: 'file', name: file.name, uid: file.uid }],
                    }));
                return false;
            },
            multiple: false,
            file: userData?.selfiePhoto,
        }),
        [userData],
    );

    const navigate = useNavigate();
    const location = useLocation();
    const userId = location.pathname.split('/')[location.pathname.split('/').length - 1];
    const datePickerRef = useRef();

    // Fetch roles, levels, and user data in parallel, then merge all for form state
    const fetchData = useCallback(async () => {
        setIsCardLoading(true);
        try {
            // Fetch roles and levels in parallel
            const [rolesRes, levelsRes] = await Promise.all([
                getViaAuth(API.roles.getAll),
                getViaAuth(API.users.levels),
            ]);
            let rolesArr: DefaultOptionType[] = [];
            let levelsArr: DefaultOptionType[] = [];
            if (responseValidator(rolesRes.status)) {
                rolesArr = rolesRes.value.items.map((i: any) => ({
                    label: i.title ?? i.name,
                    value: i.id,
                }));
            }
            if (responseValidator(levelsRes.status)) {
                levelsArr = levelsRes.value.map((i: any) => ({
                    label: i.title,
                    value: i.id ?? i.uniqueName?.id ?? i.uniqueName,
                }));
            }
            setRoles(rolesArr);
            setLevels(levelsArr);
            setLevelsData(levelsRes.value);
            // Fetch user data
            await getViaAuth(API.users.getById + `/${userId}`).then((response: any) => {
                if (responseValidator(response.status) && response.value) {
                    // Merge user roles with roles options
                    const userRoles =
                        response.value.roles?.map((r: any) => {
                            // Try to find the matching role in rolesArr
                            const found = rolesArr.find((role) => role.value === r.id);
                            return found || { label: r.title, value: r.id };
                        }) || [];
                    // Find user's current level in levelsArr
                    let userLevel = null;
                    if (response.value.level) {
                        userLevel = levelsArr.find((lvl) => lvl.value === response.value.level.id) || {
                            label: response.value.level.title,
                            value: response.value.level.id,
                        };
                    }
                    setUserData({
                        ...response.value,
                        roles: userRoles,
                        level: userLevel,
                        birthDate:
                            response.value.birthDate &&
                            moment(response.value.birthDate).locale('fa').format('YYYY/MM/DD'),
                    });
                }
            });
        } catch (e) {
            toast.error('خطا در دریافت اطلاعات کاربر');
        } finally {
            setIsCardLoading(false);
        }
    }, [userId]);

    const editUserHandle = async () => {
        if (!userData) return;
        // Map roles to role ids
        const arr = Array.isArray(userData.roles) ? userData.roles.map((i: any) => i.value || i.id) : [];
        await updateUser(setLoading, {
            ...userData,
            roles: arr,
        });
    };
    useEffect(() => {
        fetchData();
        // Only run on mount or userId change
    }, [fetchData]);

    return (
        <Card title="تغییر اطلاعات کاربر">
            <Spin spinning={isCardLoading || !userData}>
                {userData && (
                    <form>
                        <Row>
                            <Col>
                                <Input
                                    label="نام"
                                    placeholder="نام کاربر را وارد کنید"
                                    name="firstName"
                                    value={userData?.firstName || ''}
                                    onChange={(e) => setUserData({ ...userData, firstName: e.target.value })}
                                />
                            </Col>
                            <Col>
                                <Input
                                    label="نام خانوادگی"
                                    placeholder="نام خانوادگی کاربر را وارد کنید"
                                    name="lastName"
                                    value={userData?.lastName || ''}
                                    onChange={(e) => setUserData({ ...userData, lastName: e.target.value })}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Input
                                    disabled
                                    label="شماره تماس"
                                    placeholder="شماره تماس کاربر را وارد کنید"
                                    name="phoneNumber"
                                    value={userData?.phoneNumber || ''}
                                    onChange={(e) => setUserData({ ...userData, phoneNumber: e.target.value })}
                                />
                            </Col>
                            <Col>
                                <Input
                                    label="ایمیل"
                                    placeholder="ایمیل کاربر را وارد کنید"
                                    name="emailAddress"
                                    value={userData?.emailAddress || ''}
                                    onChange={(e) =>
                                        setUserData({
                                            ...userData,
                                            emailAddress: e.target.value,
                                        })
                                    }
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Input
                                    label="شماره تلفن ثابت"
                                    placeholder="شماره تلفن ثابت کاربر را وارد کنید"
                                    name="landline"
                                    value={userData?.landline || ''}
                                    onChange={(e) => setUserData({ ...userData, landline: e.target.value })}
                                />
                            </Col>
                            <Col className="has-label">
                                <label>تاریخ تولد</label>
                                <DatePicker
                                    className="date-picker"
                                    value={userData.birthDate}
                                    calendar={persian}
                                    ref={datePickerRef}
                                    locale={persian_fa}
                                    render={
                                        <div>
                                            <Input
                                                value={userData.birthDate}
                                                icon={<CalendarIcon />}
                                                placeholder="تاریخ تولد"
                                            />
                                        </div>
                                    }
                                    onChange={(e) => {
                                        setUserData({ ...userData, birthDate: e.format() });
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Input
                                    label="کد ملی"
                                    placeholder="کد ملی کاربر را وارد کنید"
                                    name="nationalCode"
                                    value={userData?.nationalCode || ''}
                                    onChange={(e) => setUserData({ ...userData, nationalCode: e.target.value })}
                                />
                            </Col>
                            <Col>
                                <Input
                                    label="رمز عبور جدید"
                                    placeholder="رمز عبور جدید کاربر را وارد کنید"
                                    name="password"
                                    type={inputType.password}
                                    htmlType={isPasswordHidden ? 'password' : 'text'}
                                    icon={
                                        isPasswordHidden ? (
                                            <EyeOutlined onClick={() => setIsPasswordHidden(false)} />
                                        ) : (
                                            <EyeInvisibleOutlined onClick={() => setIsPasswordHidden(true)} />
                                        )
                                    }
                                    value={userData?.password || ''}
                                    onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                                    maxLength={64}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Input
                                    label="کد پستی"
                                    placeholder="کد پستی کاربر را وارد کنید"
                                    name="firstName"
                                    value={userData?.postalCode || ''}
                                    onChange={(e) => setUserData({ ...userData, postalCode: e.target.value })}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className="select">
                                    <label>نقش های کاربر</label>
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        value={userData.roles?.map((role: any) => role.value || role.id)}
                                        placeholder="انتخاب کنید"
                                        onChange={(value, options) => {
                                            // value: array of selected values, options: array of option objects
                                            // We want to store the full option objects for each selected role
                                            const selectedRoles = options.map((opt: any) => ({
                                                label: opt.label,
                                                value: opt.value,
                                            }));
                                            setUserData({ ...userData, roles: selectedRoles });
                                        }}
                                        options={roles}
                                    />
                                </div>
                            </Col>
                            <Col>
                                <div className="select">
                                    <label>سطح کاربر</label>
                                    <Select
                                        value={userData.level}
                                        placeholder="انتخاب کنید"
                                        onChange={(value: any) => {
                                            // Find the selected level object from options
                                            const selected = levelsData.find((lvl) => lvl.uniqueName.id === value);
                                            setUserData({
                                                ...userData,
                                                level: {
                                                    value: selected.uniqueName.id,
                                                    title: selected.uniqueName.title,
                                                },
                                            });
                                        }}
                                        options={levelsData.map((item) => ({
                                            value: item.uniqueName.id,
                                            label: item.uniqueName.title,
                                        }))}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col className="switch-col">
                                {' '}
                                <label>نوع کاربر : </label>
                                <Switch
                                    checked={userData?.isVip}
                                    checkedChildren={'کاربر vip'}
                                    unCheckedChildren={'کاربر عادی'}
                                    onChange={(checked) =>
                                        setUserData({
                                            ...userData,
                                            isVip: checked,
                                        })
                                    }
                                />
                            </Col>
                            <Col className="switch-col">
                                {' '}
                                <label>وضعیت کاربر : </label>
                                <Switch
                                    checked={userData?.isActive}
                                    checkedChildren={'فعال'}
                                    unCheckedChildren={'غیر فعال'}
                                    onChange={(checked) =>
                                        setUserData({
                                            ...userData,
                                            isActive: checked,
                                        })
                                    }
                                />
                            </Col>
                            <Col className="switch-col">
                                {' '}
                                <label>وضعیت ورود دو مرحله ای :</label>
                                <Switch
                                    checked={userData?.isTwoStepActive}
                                    checkedChildren={'روشن'}
                                    unCheckedChildren={'خاموش'}
                                    onChange={(checked) =>
                                        setUserData({
                                            ...userData,
                                            isTwoStepActive: checked,
                                        })
                                    }
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Dragger {...fileUploadProps()}>
                                <button className="upload-button" onClick={(el) => el.preventDefault()}>
                                    <FileOutlined />
                                    <p>آپلود تصویر سلفی</p>
                                </button>
                            </Dragger>
                        </Row>

                        <div className="buttons">
                            <Button
                                htmlType="button"
                                type={buttonType.info}
                                text="اصلاح"
                                onClick={() => editUserHandle()}
                                loading={loading}
                            />

                            <Button
                                htmlType="button"
                                type={buttonType.outlined}
                                text="بازگشت"
                                onClick={() => navigate(-1)}
                            />
                        </div>
                    </form>
                )}
            </Spin>
        </Card>
    );
};
